// Packages
import { Suspense, useContext, useEffect, useState } from "react";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { useParams, useLocation } from "react-router-dom";

// Store
import {
  countryId,
  cityId,
  cuisineId,
  countryData,
  countryProps,
  cityData,
  cuisineData,
  filterHalal,
  merchantsData,
} from "../../Store/Store";

// Context
import { Language } from "../../Language/Language";

// Helpers
import { buildSelect } from "./Filters.helpers";

// Elements
import {
  Container,
  Filter,
  Label,
  Select,
  Checkbox,
  CheckboxContents,
} from "./Filters.elements";

function FiltersSuspense({ filters }) {
  return (
    <>
      <Filter>
        <Label>{filters.country}</Label>
        <Select disabled />
      </Filter>
      <Filter>
        <Label>{filters.city}</Label>
        <Select disabled />
      </Filter>
      <Filter>
        <Label>{filters.cuisine}</Label>
        <Select disabled />
      </Filter>
    </>
  );
}

function FilterStrings({ name, label, all, atom, selector }) {
  const { locale } = useParams();

  const setSelected = useSetRecoilState(atom);
  const items = useRecoilValue(selector);
  const props = useRecoilValue(countryProps);

  function onChangeHandler({ target: { value } }) {
    const selectedItem = items.find((item) => item.id === value);
    setSelected(value);
    window.tuiTrack.trackEvent('click', `filterSelect:${name}:${selectedItem?.title || value}`);
  }

  const selectElement = buildSelect({
    name,
    label,
    all,
    items: items ? items : [],
    countryId: props.key,
    onChangeHandler,
    hideSelect: (name === "city" && !props.showCityDropdown) || false,
    market: locale.replace("-", "_"),
  });

  if (!items) return null;

  return selectElement;
}

function FilterCountry({ label, showJapan }) {
  const { locale } = useParams();
  const { pathname } = useLocation();
  const isCenturion = pathname.includes('/centurion');

  const countries = useRecoilValue(countryData);
  const [id, setCountryId] = useRecoilState(countryId);
  const [hideCountrySelect, setHideCountrySelect] = useState(false);
  const props = useRecoilValue(countryProps);
  const resetCity = useResetRecoilState(cityId);
  const resetCuisine = useResetRecoilState(cuisineId);
  const localeCountry = locale.split("-")[1].toUpperCase();

  function onChangeHandler({ target }) {
    const selectedCountry = countries
      .flatMap((region) => region.countries)
      .find((country) => country.key === target.value);
    setCountryId(target.value);
    resetCity();
    resetCuisine();
    window.tuiTrack.trackEvent('click', `filterSelect:country:${selectedCountry?.title || target.value}`);
  }

  function translate(item) {
    const market = locale.replace("-", "_");
    const f = item.translations[market] || item.translations["en"];
    return f.title;
  }

  useEffect(() => {
    const codes = countries
      .map((zone) => zone.countries.map((c) => c.key))
      .flat();

    const split = locale.split("-");
    // Map en-sg to tw country
    const validLocale = locale === "en-sg" ? "TW" : (split.length > 1 ? split[1].toUpperCase() : "GB");
    const id = codes.includes(validLocale) ? validLocale : "GB";

    setCountryId(id);
  }, [countries]);

  useEffect(() => {
    if (props.key && props.key === id) {
      setHideCountrySelect(
        !isCenturion && !props.canBrowseOtherMarkets && localeCountry === id
      );
    }
  }, [props.key, props.canBrowseOtherMarkets, isCenturion, localeCountry, id]);

  return (
    <Filter style={{ display: hideCountrySelect ? "none" : "" }} id="filters">
      <Label>{label}</Label>
      {id && (
        <Select
          name="country"
          id="country"
          value={id}
          onChange={onChangeHandler}
        >
          {countries.map((region) => (
            <optgroup key={region.title} label={translate(region)}>
              {region.countries.map(
                (country) =>
                  country.isActive &&
                  (country.key === id || !hideCountrySelect) &&
                  (country.isVisibleToOtherMarkets ||
                    country.key === localeCountry) &&
                    !(locale === "en-sg" && country.key === "SG") && (
                    <option key={country.key} value={country.key}>
                      {translate(country)}
                    </option>
                  )
              )}
            </optgroup>
          ))}
        </Select>
      )}
    </Filter>
  );
}

export default function Filters() {
  const { locale } = useParams();

  const { filters } = useContext(Language);
  const id = useRecoilValue(countryId);
  const [showCuisineFilter, setShowCuisineFilter] = useState(true);
  const [showJapanInDropdown] = useState(true);
  const [regionType, setRegionType] = useState("city");
  const [regionDefault, setRegionDefault] = useState("cityDefault");
  const merchants = useRecoilValue(merchantsData(locale));
  const [filterHalalMerchants, setFilterHalalMerchants] =
    useRecoilState(filterHalal);
  const areThereHalalMerchants = hasHalalMerchants(merchants);

  function enableHalalFilter() {
    const newValue = !filterHalalMerchants;
    setFilterHalalMerchants(newValue);
    window.tuiTrack.trackEvent('click', `filterSelect:halal:${newValue}`);
  }

  function hasHalalMerchants(merchants) {
    return merchants?.some((merchant) => merchant.businessData.isHalal);
  }

  const twoFilters =
    locale === "fr-fr" || locale === "fr-ca" || locale === "en-ca";

  useEffect(() => {
    setShowCuisineFilter(id !== "JP");
    setRegionType(id === "AU" ? "state" : "city");
    setRegionDefault(id === "AU" ? "stateDefault" : "cityDefault");
  }, [id]);

  return (
    <>
      <Container id="filters">
        <Suspense fallback={<FiltersSuspense filters={filters} />}>
          <FilterCountry
            label={filters.country}
            showJapan={showJapanInDropdown}
          />
          <FilterStrings
            name="city"
            atom={cityId}
            selector={cityData}
            label={filters[regionType]}
            all={filters[regionDefault]}
          />
          {showCuisineFilter && (
            <FilterStrings
              name="cuisine"
              atom={cuisineId}
              selector={cuisineData}
              label={filters.cuisine}
              all={filters.cuisineDefault}
            />
          )}
          {twoFilters && areThereHalalMerchants && (
            <Checkbox>
              <CheckboxContents>
                <input
                  type="checkbox"
                  id="halal"
                  name="halal"
                  onClick={enableHalalFilter}
                />
              </CheckboxContents>
              <CheckboxContents>
                {locale === "en-ca" ? (
                  <label htmlFor="halal"> Halal </label>
                ) : (
                  <label htmlFor="halal"> Repas halal uniquement</label>
                )}
              </CheckboxContents>
            </Checkbox>
          )}
        </Suspense>
      </Container>
    </>
  );
}